import { useSortable } from '@dnd-kit/sortable';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { ConfigEntryType, ConfigType } from 'config/config';
import { theme } from 'config/theme';
import useUserData from 'hooks/useUserData';
import DeleteBinLineIcon from 'remixicon-react/DeleteBinLineIcon';
import { ReactComponent as EqualLineIcon } from 'assets/icons/EqualLineIcon.svg';
import { useCallback, useState } from 'react';
import { ConfigAPI, CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';

type RenderEntryValueItemProps = {
  entry: ConfigEntryType;
  isCompanyConfig: boolean;
  value: string;
};

function RenderEntryValueItem({ entry, isCompanyConfig, value }: RenderEntryValueItemProps) {
  const { companyId } = useUserData();
  const { attributes, listeners, transform, transition, setNodeRef } = useSortable({
    id: value,
  });
  const { updateConfigCache, config } = useConfigQuery({
    ...(isCompanyConfig && { companyId }),
    configName: CONFIGS.RETROSPECTIVE_PHASES.configName,
  });
  const [isDeleting, setDeleting] = useState(false);

  const onDeleteEntryValue = useCallback(async () => {
    try {
      setDeleting(true);
      const { data } = await ConfigAPI.updateConfig({
        ...config,
        ...(isCompanyConfig && {
          companyId,
        }),
        entries: (config?.entries || []).map((_entry) =>
          _entry.id === entry.id
            ? {
                ..._entry,
                entryValues: _entry.entryValues.filter(
                  (_value) => _value.toLowerCase() !== value.toLowerCase(),
                ),
              }
            : _entry,
        ),
      } as ConfigType);
      updateConfigCache({
        updatedConfig: {
          config: data?.config,
        },
      });
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  }, [config, entry, value]);

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box
        display="flex"
        alignItems="center"
        {...attributes}
        {...listeners}
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
      >
        <Box display="flex" alignItems="center" flex={1}>
          <IconButton size="small" className="sort-icon" sx={{ visibility: 'hidden' }}>
            <EqualLineIcon width={24} color={theme.palette.custom.coolGrey} />
          </IconButton>
          <Typography ml={0.5}>{value}</Typography>
        </Box>
      </Box>
      <IconButton onClick={onDeleteEntryValue}>
        {isDeleting ? (
          <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
        ) : (
          <DeleteBinLineIcon size={20} color={theme.palette.custom.redWine} />
        )}
      </IconButton>
    </Box>
  );
}

export default RenderEntryValueItem;
